'use strict';

var React = require('react');
var reactUtils = require('@sravni/react-utils');
var isRange = require('../../../utils/isRange.js');
var NumberInput = require('../../NumberInput/src/NumberInput.js');
var Slider = require('../../Slider/src/Slider.js');
var Space = require('../../Space/src/Space.js');
var SliderInput_module = require('../styles/SliderInput.module.less.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

/** Helper function normalizes range value */
const normalizeValue = (value) => {
    if (isRange.isRange(value) && (value.from ?? 0) > (value.to ?? 0)) {
        return { from: value.to, to: value.from };
    }
    return value;
};
/** Helper function for checking single mode value */
const isSingleModeValue = (mode, value) => (typeof value === 'number' || !value) && mode === 'single';
/**
 * @description Enter a number within certain range with the mouse or keyboard
 *
 * @component
 * @example
 * ```jsx
 * <SliderInput
 *    placeholder="Название поля"
 *    min={30000}
 *    max={1000000}
 *    postfix="₽/мес"
 *    thousandSeparator=" "
 *    logarithmic
 *  />
 * ```
 */
const SliderInput = React__namespace.forwardRef((props, ref) => {
    const { value, min = 0, max = 100, step = 1, invalid, disabled, logarithmic, defaultValue, readOnly, mode = 'single', placeholder, label, className, onChange, onBlur, onFocus, ...restProps } = props;
    const cx = reactUtils.useStyles(SliderInput_module.default);
    const formControlContext = reactUtils.useFormControlContext();
    const classNames = cx(className, 'container');
    const isRangeMode = mode === 'range';
    const classNamesInputWrapper = cx({
        range: isRangeMode,
    });
    const classNamesSlider = cx({
        slider: !isRangeMode,
    });
    const [innerValue, setInnerValue] = React__namespace.useState(normalizeValue(value || defaultValue));
    const isExternalChangingValue = React__namespace.useRef(false);
    const containerRef = React__namespace.useRef(null);
    const debouncedInnerValue = reactUtils.useDebounceValue(innerValue || min, 300);
    const isFirstRender = React__namespace.useRef(true);
    const handleChangeInput = (value) => {
        isExternalChangingValue.current = false;
        if (isRangeMode) {
            setInnerValue({ ...innerValue, from: value || min });
        }
        else {
            setInnerValue(value);
        }
    };
    const handleChangeInputTo = (value) => {
        isExternalChangingValue.current = false;
        if (isRangeMode && isRange.isRange(innerValue)) {
            setInnerValue({ ...innerValue, to: value || max });
        }
    };
    const handleChangeSlider = (value) => {
        isExternalChangingValue.current = false;
        !readOnly && setInnerValue(value);
    };
    const handleBlur = React__namespace.useCallback((event) => {
        const relTarget = event.relatedTarget;
        if (containerRef.current?.contains(relTarget)) {
            event.stopPropagation();
            return;
        }
        if (isRange.isRange(innerValue) && (innerValue.from ?? 0) > (innerValue.to ?? 0)) {
            // safeInvoke(onChange, normalizeValue(innerValue) || undefined);
            setInnerValue(normalizeValue(innerValue) || undefined);
        }
        onBlur?.(event);
    }, [innerValue, onBlur]);
    React__namespace.useEffect(() => {
        let newValue = value;
        if (!newValue) {
            newValue = isFirstRender.current ? defaultValue : value;
        }
        setInnerValue(newValue);
        isFirstRender.current = false;
        isExternalChangingValue.current = true;
    }, [value, defaultValue]);
    React__namespace.useEffect(() => {
        !isExternalChangingValue.current && onChange?.(debouncedInnerValue);
    }, [debouncedInnerValue, onChange]);
    return (React__namespace.createElement("div", { "data-qa": "SliderInput", className: classNames },
        React__namespace.createElement(Space.Space, { ref: containerRef, size: 8, className: classNamesInputWrapper, onBlur: handleBlur, onFocus: onFocus },
            React__namespace.createElement(NumberInput.NumberInput, { ...restProps, ref: ref, value: isRange.isRange(innerValue) ? innerValue.from : innerValue, min: min, max: max, step: step, invalid: formControlContext.invalid || invalid, disabled: formControlContext.disabled || disabled, placeholder: typeof placeholder === 'string' ? placeholder : placeholder?.from, label: typeof label === 'string' ? label : label?.from, readOnly: readOnly, format: undefined, onChange: handleChangeInput }),
            isRangeMode && (React__namespace.createElement(NumberInput.NumberInput, { value: isRange.isRange(innerValue) ? innerValue.to : '', min: min, max: max, step: step, invalid: formControlContext.invalid || invalid, disabled: formControlContext.disabled || disabled, placeholder: typeof placeholder === 'string' ? placeholder : placeholder?.to, label: typeof label === 'string' ? label : label?.to, readOnly: readOnly, format: undefined, onChange: handleChangeInputTo }))),
        isSingleModeValue(mode, innerValue) ? (React__namespace.createElement(Slider.Slider, { className: classNamesSlider, value: innerValue, min: min, max: max, step: step, mode: "single", disabled: formControlContext.disabled || disabled, logarithmic: logarithmic, onChange: handleChangeSlider })) : (React__namespace.createElement(Slider.Slider, { className: classNamesSlider, value: innerValue, min: min, max: max, step: step, mode: "range", disabled: formControlContext.disabled || disabled, logarithmic: logarithmic, onChange: handleChangeSlider }))));
});
SliderInput.defaultProps = {
    min: 0,
    max: 100,
    step: 1,
    mode: 'single',
};
// Для этого компонента при указании displayName не отображается автодокументация в сторибуке. Надо разобраться с магией
// SliderInput.displayName = 'SliderInput';

exports.SliderInput = SliderInput;
exports.normalizeValue = normalizeValue;
//# sourceMappingURL=SliderInput.js.map
